// ===============================
// App.js — STABLE (React 18 + Capacitor)
// NOTHING REMOVED — ONLY SAFETY ADDED
// ===============================

import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import theme from "./theme";
import "katex/dist/katex.min.css";

// ================= CONTEXT =================
import { AuthProvider } from "./context/AuthContext";
import { LoadingProvider, useGlobalLoading } from "./context/LoadingContext";

// ================= UI =================
import { ToastContainer } from "react-toastify";
import FullPageLoader from "./components/FullPageLoader";
//import CookieConsent from "./components/CookieConsent";

import { HelmetProvider } from "react-helmet-async";

// ================= ANALYTICS =================
import AnalyticsTracker from "./components/AnalyticsTracker";

// ================= MATHJAX =================
import { MathJaxContext } from "better-react-mathjax";

// ================= ROUTE GUARDS =================
import ProtectedRoute from "./components/ProtectedRoute";
import PublicOnlyRoute from "./components/PublicOnlyRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";

// ================= CAPACITOR =================
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";

// ================= PUBLIC PAGES =================
import LandingPage from "./pages/LandingPage";
import BlogPage from "./pages/BlogPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import WAECPage from "./pages/WaecPage";
import JAMBPage from "./pages/JAMBPage";
import NECOPage from "./pages/NECOPage";
import PublicExamPage from "./pages/PublicExamPage";
import PublicSubjects from "./pages/PublicSubjects";

// ================= EXAM SESSIONS =================
import ExamSessionPage from "./pages/exam/ExamSessionPage";
import JambExamSessionPage from "./pages/exam/JambExamSessionPage";

// ================= AUTH =================
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordSuccess from "./pages/ResetPasswordSuccess";
import EmailVerificationNotice from "./pages/EmailVerificationNotice";
import RegistrationSuccess from "./pages/RegistrationSuccess";
import GoogleSuccess from "./pages/GoogleSuccess";

// ================= MISC =================
import AboutPage from "./pages/AboutPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ContactPage from "./pages/ContactPage";
import FAQPage from "./pages/FAQPage";
import TermsPage from "./pages/TermsPage";
import PricingPublic from "./pages/PricingPublic";

// ================= STUDENT DASHBOARD =================
import DashboardLayout from "./layout/DashboardLayout";
import DashboardPage from "./pages/dashboard/DashboardPage";
import StudentResultsPage from "./pages/dashboard/StudentResultsPage";
import StudentResultDetailsPage from "./pages/dashboard/StudentResultDetailsPage";
import StudentSubjectsPage from "./pages/student/StudentSubjectsPage";
import SubjectPage from "./pages/student/ManageSubjectsPage";
import WeeklySubjectChallenge from "./pages/dashboard/WeeklySubjectChallenge";
import WeeklyPostUTMEChallenge from "./pages/dashboard/WeeklyPostUTMEChallenge";
import WeeklyMathematicsChallenge from "./pages/dashboard/WeeklyMathematicsChallenge";
import JambAnalysisPage from "./pages/jamb/JambAnalysisPage";
import SubjectLeaderboardPage from "./pages/dashboard/SubjectLeaderboardPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import MyRankPage from "./pages/dashboard/MyRankPage";
import PricingPage from "./pages/dashboard/PricingPage";
import StudentPaymentPage from "./pages/dashboard/StudentPaymentPage";
import PostUTMESpeedIntro from "./pages/dashboard/PostUTMESpeedIntro";
import SubscribeRequired from "./pages/dashboard/SubscribeRequired";
import JambHistoryPage from "./pages/dashboard/JambHistoryPage";
import JambRegisterPage from "./pages/jamb/JambRegisterPage";
import JambLeaderboardPage from "./pages/jamb/JambLeaderboardPage";

// ================= ADMIN =================
import AdminLayout from "./layout/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import UploadQuestionsPage from "./pages/admin/UploadQuestionsPage";
import ManageSubjectsPage from "./pages/admin/ManageSubjectsPage";
import AdminManageUsers from "./pages/admin/AdminManageUsers";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminQuestionsPage from "./pages/admin/AdminQuestionsPage";
import AdminTrialManagementPage from "./pages/admin/AdminTrialManagementPage";
import AdminSessions from "./pages/admin/AdminSessions";
import LiveExamControl from "./pages/admin/LiveExamControl";
import AdminContactInbox from "./pages/admin/AdminContactInbox";
import AdminSubscriptionsPage from "./pages/admin/AdminSubscriptionsPage";
import AdminWeeklyLeaderboard from "./pages/admin/AdminWeeklyLeaderboard";
import AdminLeaderboardManager from "./pages/admin/AdminLeaderboardManager";
import AdminAnnouncementEditor from "./pages/admin/AdminAnnouncementEditor";
import AdminJambHistoryPage from "./pages/admin/AdminJambHistoryPage";
import AdminPayoutsPage from "./pages/admin/AdminPayoutsPage";
import AdminAgentsPage from "./pages/admin/AdminAgentsPage";
import AdminAgentDetailPage from "./pages/admin/AdminAgentDetailPage";

// ================= AGENT =================
import AgentLayout from "./layout/AgentLayout";
import AgentDashboard from "./pages/agent/AgentDashboard";
import AgentStudents from "./pages/agent/AgentStudents";
import AgentSettings from "./pages/agent/AgentSettings";
import AgentPayoutHistory from "./pages/agent/AgentPayoutHistory";
import AgentFinancePage from "./pages/agent/AgentFinancePage";

// ================= MATHJAX CONFIG =================
const mathJaxConfig = {
  loader: { load: ["input/tex", "output/chtml"] },
  tex: {
    inlineMath: [["$", "$"], ["\\(", "\\)"]],
    displayMath: [["$$", "$$"], ["\\[", "\\]"]],
    processEscapes: true,
  },
};

// ================= ROUTES WRAPPER =================
function AppWrapper() {
  const { globalLoading } = useGlobalLoading();
  const navigate = useNavigate();
  const location = useLocation();

  // 🔎 Debug API (Browser + APK)
  console.log("API URL USED:", process.env.REACT_APP_API_URL);

  // 📱 ANDROID BACK BUTTON HANDLER (SAFE)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handler = CapacitorApp.addListener("backButton", () => {
      if (location.pathname === "/") {
        CapacitorApp.exitApp(); // exit only on home
      } else {
        navigate(-1); // normal back
      }
    });

    return () => handler.remove();
  }, [location.pathname, navigate]);

  return (
    <>
      <FullPageLoader show={globalLoading} />
      <ToastContainer />

      {/* 🌍 GLOBAL PAGE WRAPPER (Browser + APK consistency) */}
      <Box
        sx={{
          minHeight: "100vh",
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <Routes>
          {/* ========= PUBLIC ========= */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:articleId" element={<BlogArticlePage />} />
          <Route path="/waec" element={<WAECPage />} />
          <Route path="/jamb" element={<JAMBPage />} />
          <Route path="/neco" element={<NECOPage />} />
          <Route path="/practice" element={<PublicExamPage />} />
          <Route path="/subjects" element={<PublicSubjects />} />

          {/* ========= AUTH ========= */}
          <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
          <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/email-verification-notice" element={<EmailVerificationNotice />} />
          <Route path="/registration-success" element={<RegistrationSuccess />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-success" element={<ResetPasswordSuccess />} />
          <Route path="/google-success" element={<GoogleSuccess />} />

          {/* ========= EXAM ========= */}
          <Route
            path="/exam/session/:examId"
            element={
              <ProtectedRoute role="student">
                <ExamSessionPage />
              </ProtectedRoute>
            }
          />
          <Route path="/exam/jamb/session/:examId" element={<JambExamSessionPage />} />

          {/* ========= AGENT ========= */}
          <Route path="/agent" element={<ProtectedRoute role="agent"><AgentLayout /></ProtectedRoute>}>
            <Route index element={<AgentDashboard />} />
            <Route path="students" element={<AgentStudents />} />
            <Route path="settings" element={<AgentSettings />} />
            <Route path="payout-history" element={<AgentPayoutHistory />} />
            <Route path="finance" element={<AgentFinancePage />} />
          </Route>

          {/* ========= STUDENT ========= */}
          <Route path="/dashboard" element={<ProtectedRoute role="student"><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<DashboardPage />} />
            <Route path="subjects" element={<StudentSubjectsPage />} />
            <Route path="results" element={<StudentResultsPage />} />
            <Route path="result/:examId" element={<StudentResultDetailsPage />} />
            <Route path="manage-subject" element={<SubjectPage />} />
            <Route path="weekly-subject-challenge" element={<WeeklySubjectChallenge />} />
            <Route path="weekly-post-utme-challenge" element={<WeeklyPostUTMEChallenge />} />
            <Route path="weekly-mathematics-challenge" element={<WeeklyMathematicsChallenge />} />
            <Route path="jamb-analysis/:attemptId" element={<JambAnalysisPage />} />
            <Route path="post-utme-speed-test" element={<PostUTMESpeedIntro />} />
            <Route path="leaderboard/subject/:subjectId" element={<SubjectLeaderboardPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="my-rank" element={<MyRankPage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="my-subscriptions" element={<StudentPaymentPage />} />
            <Route path="subscribe-required" element={<SubscribeRequired />} />
            <Route path="jamb/my-jamb-history" element={<JambHistoryPage />} />
            <Route path="jamb/jamb-learderboard" element={<JambLeaderboardPage />} />
            <Route path="jamb-challenge" element={<JambRegisterPage />} />
          </Route>

          {/* ========= ADMIN ========= */}
          <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminManageUsers />} />
            <Route path="subjects" element={<ManageSubjectsPage />} />
            <Route path="questions" element={<UploadQuestionsPage />} />
            <Route path="manage-questions" element={<AdminQuestionsPage />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="trials" element={<AdminTrialManagementPage />} />
            <Route path="sessions" element={<AdminSessions />} />
            <Route path="live-exams" element={<LiveExamControl />} />
            <Route path="contacts" element={<AdminContactInbox />} />
            <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
            <Route path="weekly-leaderboards" element={<AdminWeeklyLeaderboard />} />
            <Route path="leaderboard-manager" element={<AdminLeaderboardManager />} />
            <Route path="weekly-announcement" element={<AdminAnnouncementEditor />} />
            <Route path="jamb/jamb-history" element={<AdminJambHistoryPage />} />
            <Route path="payouts" element={<AdminPayoutsPage />} />
            <Route path="agents" element={<AdminAgentsPage />} />
            <Route path="agents/:agent_id" element={<AdminAgentDetailPage />} />
          </Route>

          {/* ========= MISC ========= */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/pricing" element={<PricingPublic />} />

          {/* ========= 404 ========= */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>

      {/*   <CookieConsent /> */}
    </>
  );
}

// ================= MAIN APP =================
export default function App() {
  return (
    <HelmetProvider>
      <LoadingProvider>
        <AuthProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <MathJaxContext version={3} config={mathJaxConfig}>
              <BrowserRouter>
                <AnalyticsTracker />
                <AppWrapper />
              </BrowserRouter>
            </MathJaxContext>
          </ThemeProvider>
        </AuthProvider>
      </LoadingProvider>
    </HelmetProvider>
  );
}
